import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SEO } from "@/components/SEO";
import { 
  Zap, 
  Search, 
  BarChart3, 
  Clock, 
  Server, 
  Code2, 
  Database, 
  Rocket, 
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Info
} from "lucide-react";
import { Link } from "wouter";

export default function TTFBAudit() {
  const [url, setUrl] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyze = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;
    setIsAnalyzing(true);
    // Simulation of analysis
    setTimeout(() => {
      setIsAnalyzing(false);
      // In a real app, we would redirect or show results
      alert("Analysis simulation complete. In a real environment, this would trigger a back-end audit of " + url);
    }, 2000);
  };

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <SEO 
        title="TTFB Audit & Optimization | AccelerateByte"
        description="Analyze and reduce your Time to First Byte (TTFB). Improve server response time to enhance user experience and SEO performance."
      />

      {/* 1. HERO / ANALYZER */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-background to-background"></div>
        
        <div className="container relative z-10 mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-sm font-medium text-blue-400 mb-8"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Performance Audit
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-6xl font-bold tracking-tight mb-6"
          >
            Time to First Byte (TTFB)
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12"
          >
            TTFB is the time it takes for the browser to receive the first byte in response to a request. 
            A critical metric for SEO and user experience.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-3xl mx-auto"
          >
            <form onSubmit={handleAnalyze} className="relative flex flex-col md:flex-row gap-4 p-2 rounded-2xl bg-card border border-border/50 shadow-2xl backdrop-blur-sm">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input 
                  type="url" 
                  placeholder="Enter URL to Analyze (e.g., https://yourwebsite.com)" 
                  className="w-full h-14 pl-12 pr-4 bg-transparent border-none focus:ring-0 text-foreground placeholder:text-muted-foreground/60"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                />
              </div>
              <button 
                type="submit" 
                disabled={isAnalyzing}
                className="h-14 px-8 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2 overflow-hidden relative"
              >
                <AnimatePresence mode="wait">
                  {isAnalyzing ? (
                    <motion.div 
                      key="loading"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -20, opacity: 0 }}
                      className="flex items-center gap-2"
                    >
                      <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Analyzing...
                    </motion.div>
                  ) : (
                    <motion.div 
                      key="ready"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -20, opacity: 0 }}
                      className="flex items-center gap-2"
                    >
                      <Zap className="h-5 w-5" />
                      Analyze
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* 2. OVERVIEW CARDS */}
      <section className="py-20 px-4 container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm"
          >
            <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500 mb-6">
              <Clock className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold mb-4">What is TTFB?</h3>
            <p className="text-muted-foreground leading-relaxed">
              Time to First Byte (TTFB) is the measurement used as an indication of the responsiveness of a webserver or other network resource. It's the total time from the request being made to the first byte of data being received by the client.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm"
          >
            <div className="h-12 w-12 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-500 mb-6">
              <Zap className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold mb-4">Critical for Experience</h3>
            <p className="text-muted-foreground leading-relaxed">
              Reducing TTFB is critical as it affects every resource referenced in your HTML. A slow TTFB can result in visitors seeing a blank page, leading to frustration and higher bounce rates.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="p-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm"
          >
            <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center text-green-500 mb-6">
              <Server className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold mb-4">Back-end Optimization</h3>
            <p className="text-muted-foreground leading-relaxed">
              Primarily a back-end optimization, reducing TTFB complements front-end development to significantly improve overall site performance and Core Web Vitals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 3. DETAILED EXPLANATION */}
      <section className="py-24 px-4 bg-muted/20 border-y border-border/50">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="w-full lg:w-1/2">
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-8">How server response time affects performance</h2>
              <div className="space-y-6">
                <p className="text-lg text-muted-foreground">
                  When a user requests your page, this request needs to be sent to the server, and the server needs to generate an appropriate response and send back your page resources.
                </p>
                <div className="flex gap-4 items-start p-6 rounded-xl bg-background border border-border/50">
                  <AlertCircle className="h-6 w-6 text-yellow-500 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-2">The Blank Screen Effect</h4>
                    <p className="text-sm text-muted-foreground">
                      A slow TTFB significantly impacts your website performance; your visitors will see a blank screen while waiting for your server to respond. This often results in users leaving before the page even begins to load.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start p-6 rounded-xl bg-background border border-border/50">
                  <CheckCircle2 className="h-6 w-6 text-green-500 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-2">The Rapid Response Benefit</h4>
                    <p className="text-sm text-muted-foreground">
                      A fast TTFB means your visitor receives a rapid response. The faster the first byte arrives, the sooner the rest of your page resources can be processed and rendered.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="w-full lg:w-1/2">
              <div className="relative p-8 rounded-3xl bg-background/40 backdrop-blur-xl border border-white/10 overflow-hidden shadow-2xl group">
                <div className="absolute inset-0 z-0 opacity-20 group-hover:opacity-30 transition-opacity duration-700">
                  <img 
                    src="/images/performance-audit.png" 
                    alt="Performance Dashboard Visualization" 
                    className="w-full h-full object-cover scale-110 group-hover:scale-100 transition-transform duration-1000"
                  />
                </div>
                
                <div className="relative z-10">
                  <h3 className="font-display text-2xl font-bold mb-6 flex items-center gap-3">
                    <BarChart3 className="h-6 w-6 text-primary" />
                    How TTFB is Calculated
                  </h3>
                
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-background/50 border border-border/50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Redirect Duration</span>
                      <span className="text-xs text-muted-foreground">Time spent on redirects</span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 w-[15%]"></div>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-background/50 border border-border/50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Connection Duration</span>
                      <span className="text-xs text-muted-foreground">DNS + TCP + SSL</span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500 w-[25%]"></div>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-background/80 border border-primary/30 shadow-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold text-primary">Backend Duration</span>
                      <span className="text-xs font-bold text-primary uppercase">Critical Factor</span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 w-[60%]"></div>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground italic">
                      This audit focuses on the Backend Duration, which contributes most heavily to your overall TTFB.
                    </p>
                  </div>

                  <div className="pt-4 flex items-center justify-between text-sm font-bold">
                    <span>Total TTFB</span>
                    <span className="text-primary">Redirect + Connection + Backend</span>
                  </div>
                </div>

                <div className="mt-8 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex gap-3 items-center">
                  <Info className="h-5 w-5 text-yellow-500 shrink-0" />
                  <p className="text-xs text-yellow-500/90 font-medium">
                    Audit Trigger: This audit is triggered if the initial server response time exceeds 600 ms.
                  </p>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. STRATEGIES */}
      <section className="py-24 px-4 container mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">How to reduce server response times?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">Implementing these core strategies can lead to near-instantaneous page loads.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Strategy 1 */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col gap-6"
          >
            <div className="h-16 w-16 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
              <Code2 className="h-8 w-8" />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4">1) Optimizing your application code</h3>
              <p className="text-muted-foreground mb-6">
                The application code and its interaction with the database is crucial. Inefficient logic can add hundreds of milliseconds to every request.
              </p>
              <ul className="space-y-4">
                {[
                  "Streamlining the codebase for maximum function efficiency",
                  "Reducing complex operations or bundling code to execute only when needed",
                  "Optimizing database queries and removing unnecessary database hits",
                  "Implementing code profiling to identify bottleneck hot-spots"
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <CheckCircle2 className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                    <span className="text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-6 rounded-2xl bg-blue-500/5 border border-blue-500/20">
                <p className="text-sm italic text-muted-foreground">
                  "We strongly recommend soliciting developer assistance as you are dealing with the core logic of your website."
                </p>
              </div>
            </div>
          </motion.div>

          {/* Strategy 2 */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col gap-6"
          >
            <div className="h-16 w-16 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-500">
              <Database className="h-8 w-8" />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4">2) Implementing server-side caching</h3>
              <p className="text-muted-foreground mb-6">
                Caching serves a pre-generated version of your page, bypassing the need for the server to process and generate the page on every demand.
              </p>
              <div className="space-y-6">
                <div className="p-6 rounded-2xl border border-border/50 bg-card/30">
                  <h4 className="font-bold mb-2 flex items-center gap-2">
                    <Server className="h-4 w-4 text-purple-500" />
                    Server-Level Caching
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Your host may already offer server-side caching solutions like Varnish, Redis, or Nginx FastCGI cache. Reach out to them to clarify and enable these features.
                  </p>
                </div>
                <div className="p-6 rounded-2xl border border-purple-500/20 bg-purple-500/5">
                  <h4 className="font-bold mb-2 flex items-center gap-2">
                    <Rocket className="h-4 w-4 text-purple-500" />
                    CMS & WordPress Optimization
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    CMS users can implement page caching through specialized plugins. For WordPress, solutions like WP Rocket can reduce TTFB instantly out of the box.
                  </p>
                  <Link href="/contact" className="text-xs font-bold text-purple-500 flex items-center gap-1 hover:gap-2 transition-all">
                    GET HELP WITH CMS OPTIMIZATION <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 5. CTA SECTION */}
      <section className="py-24 px-4 bg-gradient-to-br from-blue-900/40 via-background to-purple-900/40 border-t border-border/50">
        <div className="container mx-auto text-center max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-12 md:p-20 rounded-[3rem] bg-background/40 backdrop-blur-xl border border-white/10 shadow-3xl"
          >
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-6 tracking-tight">I want to optimize my website performance</h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Our elite engineering team specializes in surgical performance optimizations that slash TTFB and boost your rankings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact" className="h-14 px-10 rounded-xl bg-blue-600 text-white font-bold text-lg transition-all hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] flex items-center justify-center gap-2">
                Start Optimization
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link href="/services/website-development" className="h-14 px-10 rounded-xl border border-white/10 bg-white/5 backdrop-blur-md text-white font-bold text-lg transition-all hover:bg-white/10 flex items-center justify-center">
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
